'use client';

import type { ReactNode } from 'react';
import * as React from 'react';
import { z } from 'zod';
import { CookieConsentContext } from './cookie-consent-context';
import { useCookies } from './cookie-consent-hooks';
import type { ConsentCategories } from '@/lib/consent/gtag';
import { updateConsent } from '@/lib/consent/gtag';

// Zod schema for validating consent categories
const consentCategoriesSchema = z.object({
  functionality: z.enum(['granted', 'denied']),
  analytics: z.enum(['granted', 'denied']),
  ads: z.enum(['granted', 'denied']),
});

type CookieConsentProviderProps = {
  children: ReactNode;
};

export function CookieConsentProvider({
  children,
}: CookieConsentProviderProps) {
  const { getCookie } = useCookies();

  const parseConsentCookie = React.useCallback(
    (consentCookie: string | undefined): ConsentCategories | null => {
      if (!consentCookie) {
        return null;
      }

      // Parse JSON first (this can throw)
      let parsed: unknown;
      try {
        parsed = JSON.parse(consentCookie);
      } catch {
        // Invalid JSON format
        return null;
      }

      // Validate structure with Zod (safeParse doesn't throw)
      const validationResult = consentCategoriesSchema.safeParse(parsed);
      if (validationResult.success) {
        return validationResult.data;
      }

      // Invalid schema/structure - force re-consent by returning null
      return null;
    },
    [],
  );

  // Lazy initialization - runs only once when component mounts
  const [consentCategories, setConsentCategories] =
    React.useState<ConsentCategories | null>(() => {
      const consentCookie = getCookie('consent');
      return parseConsentCookie(consentCookie);
    });

  React.useEffect(() => {
    // On initial load, if we have stored consent from a previous session,
    // we need to update Google's consent state to reflect those choices.
    if (consentCategories) {
      updateConsent(consentCategories);
    }
  }, [consentCategories]);

  // Simple update function without side effects
  const updateConsentCategories = React.useCallback(
    (categories: ConsentCategories) => {
      setConsentCategories(categories);
    },
    [],
  );

  const value: CookieConsentContext = {
    consentCategories,
    updateConsentCategories,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}
